import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../store/AuthContext";
import { PosService } from "../services";
import { currentBusinessDate, isInBusinessDay } from "../utils/businessDay";

/**
 * Cashier session gate (business-day aware).
 *
 * A session is "valid for today's sale" only when:
 *   • status === "OPEN", AND
 *   • openedAt falls inside the CURRENT business day window
 *     (03:00 of the business date → 02:10 of the next calendar date).
 *
 * A session opened yesterday that's still OPEN at 03:01 the next day is
 * treated as expired and the cashier must close + open a fresh one.
 */
export const useCashierSessionGate = ({
  redirect = false,
  sessionPath = "/cashier/cash-session",
} = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    PosService.cashCurrent(user.userId)
      .then((s) => {
        if (cancelled) return;
        const businessDate = currentBusinessDate();
        const ok = s && s.status === "OPEN" && isInBusinessDay(s.openedAt, businessDate);
        setSession(ok ? s : null);
        if (!ok && redirect) navigate(sessionPath, { replace: true });
      })
      .catch(() => setSession(null))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [user.userId, redirect, sessionPath, navigate]);

  return { session, loading, requiresSession: !session };
};
